import { useCallback, useEffect, useRef, useState } from 'react';
import usePlayerEvent from '../_hooks/use-player-event';
import { AUTO_HIDE_TIMEOUT } from '../player-helpers';
import { PlayerEvent } from '../types';
import { useAtom } from 'jotai';
import { playerWaitingAtom, playerOpenedAtom } from '../atoms';
import { usePlayerContext } from '../_contexts/player-context';
import { LiveTvChannel } from '@smart-tv/typings/content';
import useSpinnerTimeout from './use-spinner-timeout';

interface UsePlayerLoadingParams {
  channel?: LiveTvChannel;
  mediaId?: number;
  eventId?: number;
  contentId?: number;
}

const usePlayerLoading = (params: UsePlayerLoadingParams = {}) => {
  const { channel, mediaId, eventId, contentId } = params;
  const loadingRootRef = useRef<HTMLDivElement>(null);

  const { player, onSetFirstTimeLoading } = usePlayerContext();

  const [playerOpened, setPlayerOpened] = useAtom(playerOpenedAtom);
  const [showSpinner, setShowSpinner] = useState(false);
  const [playerWaiting, setPlayerWaiting] = useAtom(playerWaitingAtom);

  const firstTimeLoadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const spinnerShowTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { startSpinnerTimeout, clearSpinnerTimeout } = useSpinnerTimeout({
    channel,
    mediaId,
    eventId,
    contentId
  });

  const clearSpinnerShowTimeout = useCallback(() => {
    if (spinnerShowTimeoutRef.current !== null) {
      clearTimeout(spinnerShowTimeoutRef.current);
      spinnerShowTimeoutRef.current = null;
    }
  }, []);

  const scheduleSpinnerDisplay = useCallback(() => {
    clearSpinnerShowTimeout();
    setShowSpinner(false);
    spinnerShowTimeoutRef.current = setTimeout(() => {
      spinnerShowTimeoutRef.current = null;
      setShowSpinner(true);
    }, 200);
  }, [clearSpinnerShowTimeout]);

  const handlePlayerWaiting = useCallback(() => {
    if (player && player.isPaused()) return;

    loadingRootRef.current?.focus();
    setPlayerWaiting(true);
    // dont debounce the initial loading spinner
    scheduleSpinnerDisplay();
    startSpinnerTimeout('buffering');
  }, [setPlayerWaiting, scheduleSpinnerDisplay, startSpinnerTimeout, player]);

  const handlePlayerOpened = useCallback(() => {
    setPlayerOpened(true);
    clearSpinnerTimeout();
    firstTimeLoadingTimeoutRef.current = setTimeout(() => {
      if (player) {
        onSetFirstTimeLoading(false);
      }
    }, AUTO_HIDE_TIMEOUT);
  }, [onSetFirstTimeLoading, player, clearSpinnerTimeout, setPlayerOpened]);

  const handlePlayerLoadingEnded = useCallback(() => {
    clearSpinnerShowTimeout();
    setShowSpinner(false);
    setPlayerWaiting(false);
    clearSpinnerTimeout();
  }, [clearSpinnerShowTimeout, setPlayerWaiting, clearSpinnerTimeout]);

  usePlayerEvent(PlayerEvent.Playing, handlePlayerLoadingEnded);
  usePlayerEvent(PlayerEvent.Paused, handlePlayerLoadingEnded);
  usePlayerEvent(PlayerEvent.Waiting, handlePlayerWaiting);
  usePlayerEvent(PlayerEvent.Opened, handlePlayerOpened);

  useEffect(() => {
    startSpinnerTimeout('initial_load');
    return () => {
      clearSpinnerShowTimeout();
      clearSpinnerTimeout();
      if (firstTimeLoadingTimeoutRef.current !== null) {
        clearTimeout(firstTimeLoadingTimeoutRef.current);
      }
    };

    // This should only run on mount without exceptions
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    playerOpened,
    playerWaiting,
    loadingRootRef,
    showSpinner
  };
};

export default usePlayerLoading;
